import React from "react";
import {Button} from "antd";
import {EyeOutlined} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {APPLICATION_TYPES} from "constants/application";
import {ApplicationStatusTag} from "../common/ApplicationStatusTag";
import {ApplicationElapsedTime} from "../common/ApplicationElapsedTime";
import {
    formatDate,
    formatDateTime,
    getCityDistrictLabel,
    getDetailRoute,
    getZoneManagementLabel
} from "../common/applicationUtils";

export const ApplicationsTable = ({type, rows, total, loading, pagination, onPageChange}) => {
    const {t} = useTranslation(["applications"]);
    const label = key => t(`list.columns.${key}`, {ns: "applications"});
    const cell = key => () => ({"data-label": label(key)});
    const isCompany = type === APPLICATION_TYPES.company;

    const column = (key, render) => ({title: label(key), key, render, onCell: cell(key)});

    const columns = [
        column("detail", (_, record) => (
            <Link to={getDetailRoute(type, record._id)} target="_blank" rel="noopener noreferrer">
                <Button type="link" size="small" icon={<EyeOutlined />} />
            </Link>
        )),
        column("date", (_, record) => formatDate(record.createdAt)),
        ...(isCompany ? [] : [column("agencyOfficer", (_, record) => record.fullName)]),
        column("zoneManagement", (_, record) => getZoneManagementLabel(record)),
        column("cityDistrict", (_, record) => (
            <div style={{maxWidth: 240}}>
                <Shortener text={getCityDistrictLabel(record, type)} length={48} />
            </div>
        )),
        ...(isCompany
            ? [
                  column("fullName", (_, record) => record.fullName),
                  column("companyName", (_, record) => record.companyName)
              ]
            : []),
        column("lastAction", (_, record) => formatDateTime(record.updatedAt || record.createdAt)),
        column("status", (_, record) => <ApplicationStatusTag status={record.status} />),
        column("elapsed", (_, record) => <ApplicationElapsedTime application={record} />)
    ];

    return (
        <ResponsiveDataTable
            size="small"
            columns={columns}
            dataSource={rows}
            rowKey="_id"
            loading={loading}
            scroll={{x: "max-content"}}
            pagination={{
                hideOnSinglePage: true,
                current: Math.floor(pagination.skip / pagination.limit) + 1,
                pageSize: pagination.limit,
                total,
                showTotal: count => t("list.total", {ns: "applications", total: count}),
                onChange: onPageChange
            }}
        />
    );
};
